import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useMailer } from '../hooks/useMailer';
import '../style.css';

const positions = [
  {
    icon: '👩‍🏫',
    title: 'Maestro(a) de Inglés',
    type: 'Presencial',
    tags: ['Certificación requerida', 'Tlaquepaque'],
    desc: 'Imparte clases a grupos de diferentes niveles y edades en nuestras instalaciones. Diseña planeaciones, evalúa y acompaña a los alumnos con el respaldo de LinguaHub y Spark.',
    requirements: ['Certificación en enseñanza del inglés (CELTA, TESOL o equivalente)', 'Nivel B2 o superior comprobable', 'Experiencia previa en docencia deseable', 'Actitud dinámica y vocación de servicio'],
  },
  {
    icon: '💻',
    title: 'Maestro(a) Online',
    type: 'Remoto',
    tags: ['100% Online', 'Flexible'],
    desc: 'Imparte clases de inglés en formato virtual a alumnos individuales o grupos pequeños. Horarios flexibles y soporte tecnológico completo.',
    requirements: ['Nivel B2 o superior comprobable', 'Experiencia en clases virtuales', 'Conexión estable a internet y equipo adecuado', 'Certificación en inglés deseable'],
  }
];

const perks = [
  { icon: '📚', title: 'Clases de inglés gratis', desc: 'Acceso a cursos de inglés para ti y tu familia como beneficio institucional.' },
  { icon: '🏆', title: 'Plan de desarrollo', desc: 'Capacitaciones, talleres y oportunidades de crecimiento dentro de la institución.' },
  { icon: '🤝', title: 'Ambiente colaborativo', desc: 'Equipo humano, profesional y comprometido con la educación de calidad.' },
  { icon: '📱', title: 'Herramientas digitales', desc: 'Acceso a LinguaHub, LinguaSkills, Spark y recursos tecnológicos de primera línea.' },
  { icon: '⏰', title: 'Horarios flexibles', desc: 'Para docentes online, posibilidad de construir tu propio horario según disponibilidad.' },
  { icon: '📍', title: 'Ubicación accesible', desc: 'Instalaciones en el centro de Tlaquepaque, bien comunicadas y con buen ambiente de trabajo.' },
];

function WorkWithUs() {
  const { send, loading, submitted, ErrorBox } = useMailer('postulacion');
  const [selectedPosition, setSelectedPosition] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    send(Object.fromEntries(new FormData(e.target).entries()));
  };

  return (
    <div className="page-wrapper">
      <Helmet>
        <title>Trabaja con Nosotros | Culturlingua — Únete al equipo</title>
        <meta name="description" content="Únete al equipo de Culturlingua. Buscamos maestros de inglés, coordinadores y más. Más de 20 años formando hablantes reales en Tlaquepaque." />
      </Helmet>

      {/* Hero */}
      <div className="page-hero">
        <div className="section-tag">Únete al equipo</div>
        <h1>Trabaja con Nosotros</h1>
        <p>Sé parte de una institución con más de 20 años formando hablantes reales de inglés en Jalisco.</p>
      </div>

      {/* Por qué unirte */}
      <section style={{ background: 'var(--cream)', padding: '80px 48px' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '56px' }}>
            <div className="section-tag">¿Por qué Culturlingua?</div>
            <h2 className="section-headline">Lo que te espera aquí</h2>
            <p className="section-sub" style={{ margin: '0 auto' }}>
              En Culturlingua creemos que un gran equipo hace grandes estudiantes. Por eso invertimos en las personas que trabajan con nosotros.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
            {perks.map((perk, i) => (
              <div key={i} className="modal-card">
                <div className="modal-card-accent"></div>
                <div className="modal-card-body">
                  <div style={{ fontSize: '2rem', marginBottom: '12px' }}>{perk.icon}</div>
                  <h3 style={{ marginBottom: '8px' }}>{perk.title}</h3>
                  <p>{perk.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Posiciones abiertas */}
      <section style={{ background: 'white', padding: '80px 48px' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '56px' }}>
            <div className="section-tag">Vacantes actuales</div>
            <h2 className="section-headline">Posiciones disponibles</h2>
            <p className="section-sub" style={{ margin: '0 auto' }}>
              ¿No encuentras tu perfil exacto? Escríbenos de todas formas — siempre estamos abiertos a conocer a personas talentosas.
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {positions.map((pos, i) => (
              <div
                key={i}
                style={{
                  background: 'var(--cream)',
                  border: '1px solid rgba(0,0,0,0.07)',
                  borderRadius: '20px',
                  padding: '36px',
                  transition: 'all 0.3s ease',
                }}
              >
                <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <div style={{ fontSize: '2.8rem', lineHeight: 1 }}>{pos.icon}</div>
                  <div style={{ flex: 1, minWidth: '220px' }}>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                      <span style={{
                        background: 'var(--blue)',
                        color: 'white',
                        padding: '2px 10px',
                        borderRadius: '100px',
                        fontSize: '0.75rem',
                        fontWeight: '700',
                      }}>{pos.type}</span>
                      {pos.tags.map((tag, ti) => (
                        <span key={ti} style={{
                          background: 'rgba(59,111,224,0.08)',
                          color: 'var(--blue)',
                          border: '1px solid rgba(59,111,224,0.2)',
                          padding: '2px 10px',
                          borderRadius: '100px',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                        }}>{tag}</span>
                      ))}
                    </div>
                    <h3 style={{ fontSize: '1.3rem', marginBottom: '8px', color: 'var(--dark)' }}>{pos.title}</h3>
                    <p style={{ color: 'var(--muted)', lineHeight: '1.7', marginBottom: '16px' }}>{pos.desc}</p>
                    <div>
                      <p style={{ fontWeight: '700', fontSize: '0.85rem', color: 'var(--dark)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Buscamos:</p>
                      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {pos.requirements.map((req, ri) => (
                          <li key={ri} style={{ display: 'flex', gap: '8px', color: 'var(--muted)', fontSize: '0.92rem' }}>
                            <span style={{ color: 'var(--blue)', fontWeight: '700', flexShrink: 0 }}>→</span> {req}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedPosition(pos.title);
                        document.getElementById('apply-form').scrollIntoView({ behavior: 'smooth' });
                      }}
                      className="btn-glow"
                      style={{ marginTop: '24px', display: 'inline-block', cursor: 'pointer', border: 'none' }}
                    >
                      Postularme a esta vacante →
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Formulario */}
      <section id="apply-form" style={{ background: 'var(--cream)', padding: '80px 48px' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <div className="section-tag">Postúlate</div>
            <h2 className="section-headline">Envíanos tu solicitud</h2>
            <p className="section-sub" style={{ margin: '0 auto' }}>
              Completa el formulario y nos pondremos en contacto contigo en un plazo de 5 días hábiles.
            </p>
          </div>

          {submitted ? (
            <div style={{
              background: 'var(--navy)',
              borderRadius: '24px',
              padding: '60px 48px',
              textAlign: 'center',
              color: 'white',
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '16px' }}>🎉</div>
              <h3 style={{ fontFamily: 'Fraunces, serif', fontSize: '2rem', marginBottom: '12px' }}>¡Solicitud enviada!</h3>
              <p style={{ color: 'rgba(255,255,255,0.6)', lineHeight: '1.7' }}>
                Gracias por tu interés en Culturlingua. Revisaremos tu información y te contactaremos pronto.
              </p>
            </div>
          ) : (
            <div style={{ background: 'white', borderRadius: '24px', padding: '48px', boxShadow: '0 4px 24px rgba(0,0,0,0.07)' }}>
              <form onSubmit={handleSubmit}>
                <div className="form-row" style={{ marginBottom: '16px' }}>
                  <div className="form-field" style={{ marginBottom: 0 }}>
                    <label className="form-label">Nombre completo *</label>
                    <input className="form-input" type="text" name="nombre" placeholder="Tu nombre" required />
                  </div>
                  <div className="form-field" style={{ marginBottom: 0 }}>
                    <label className="form-label">WhatsApp *</label>
                    <input className="form-input" type="tel" name="telefono" placeholder="+52 33..." required />
                  </div>
                </div>

                <div className="form-field">
                  <label className="form-label">Correo electrónico *</label>
                  <input className="form-input" type="email" name="correo" placeholder="tu@correo.com" required />
                </div>

                <div className="form-field">
                  <label className="form-label">Vacante de interés *</label>
                  <select
                    className="form-input"
                    name="puesto"
                    value={selectedPosition}
                    onChange={e => setSelectedPosition(e.target.value)}
                    required
                    style={{ appearance: 'none', cursor: 'pointer' }}
                  >
                    <option value="">Selecciona una vacante...</option>
                    {positions.map((p, i) => (
                      <option key={i} value={p.title}>{p.title}</option>
                    ))}
                    <option value="Otra / Espontánea">Otra / Postulación espontánea</option>
                  </select>
                </div>

                <div className="form-field">
                  <label className="form-label">Nivel de inglés</label>
                  <select className="form-input" name="nivel_ingles" style={{ appearance: 'none', cursor: 'pointer' }}>
                    <option value="">Selecciona tu nivel...</option>
                    <option value="A1-A2">A1–A2 (Básico)</option>
                    <option value="B1-B2">B1–B2 (Intermedio)</option>
                    <option value="C1">C1 (Avanzado)</option>
                    <option value="C2">C2 (Nativo/Bilingüe)</option>
                  </select>
                </div>

                <div className="form-field">
                  <label className="form-label">Cuéntanos sobre ti *</label>
                  <textarea
                    className="form-input form-textarea"
                    name="pregunta"
                    placeholder="Experiencia relevante, por qué te interesa Culturlingua, disponibilidad de horario..."
                    required
                    rows={5}
                  ></textarea>
                </div>

                <div className="form-field">
                  <label className="form-label">Link a CV o LinkedIn (opcional)</label>
                  <input className="form-input" type="url" name="cv_link" placeholder="https://..." />
                </div>

                {ErrorBox}
                <button
                  type="submit"
                  className="form-submit"
                  disabled={loading}
                  style={{ opacity: loading ? 0.7 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                >
                  {loading ? 'Enviando...' : 'Enviar mi solicitud →'}
                </button>

                <p style={{ textAlign: 'center', color: 'var(--muted)', fontSize: '0.82rem', marginTop: '16px' }}>
                  También puedes escribirnos directamente a{' '}
                  <a href="mailto:administracion@culturlingua.com" style={{ color: 'var(--blue)' }}>
                    administracion@culturlingua.com
                  </a>
                </p>
              </form>
            </div>
          )}
        </div>
      </section>

      {/* CTA final */}
      <section style={{ background: 'var(--navy)', padding: '80px 48px', textAlign: 'center' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <h2 style={{ fontFamily: 'Fraunces, serif', fontSize: '2.2rem', color: 'white', fontWeight: '900', marginBottom: '16px' }}>
            ¿Tienes dudas?
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '32px' }}>
            Escríbenos por WhatsApp o visítanos directamente en Tlaquepaque. Estamos para orientarte.
          </p>
          <a
            href="https://wa.me/5213333449139?text=Hola%2C%20quisiera%20información%20sobre%20las%20vacantes%20disponibles."
            target="_blank"
            rel="noopener noreferrer"
            className="btn-glow"
          >
            Escribir por WhatsApp →
          </a>
        </div>
      </section>
    </div>
  );
}

export default WorkWithUs;